'use client';

import { useState, useEffect } from 'react';
import {
  QuestionnaireStep,
  QuestionnaireState,
  OrganizationContext,
  AISystem,
  AISystemCharacteristics,
  ComplianceStatus,
  TimelineResources,
  OrganizationLocation,
  OrganizationSize,
  OrganizationRole,
  IndustrySector,
  AISystemCategory,
  EUAIActAssessment,
} from '@/lib/eu-ai-act-types';
import { INDUSTRY_AI_USE_CASES } from '@/lib/eu-ai-act-requirements';

const STORAGE_KEY = 'eu-ai-act-assessment';
const TOTAL_STEPS = 6;

interface Props {
  onComplete: (assessment: EUAIActAssessment) => void;
}

// Default AI system characteristics (all false)
const defaultCharacteristics: AISystemCharacteristics = {
  socialScoring: false,
  realTimeBiometricPublic: false,
  emotionRecognitionWorkplace: false,
  subliminalManipulation: false,
  exploitsVulnerabilities: false,
  predictivePolicingIndividual: false,
  facialRecognitionScraping: false,
  biometricCategorization: false,
  biometricIdentification: false,
  criticalInfrastructure: false,
  educationAssessment: false,
  employmentDecisions: false,
  essentialServicesAccess: false,
  creditScoring: false,
  emergencyServices: false,
  lawEnforcementUse: false,
  migrationAsylum: false,
  justiceAdministration: false,
  democraticProcesses: false,
  interactsWithHumans: false,
  generatesContent: false,
  emotionRecognition: false,
  deepfakeGeneration: false,
};

export default function EUAIActQuestionnaire({ onComplete }: Props) {
  const [currentStep, setCurrentStep] = useState<QuestionnaireStep>(1);
  const [organization, setOrganization] = useState<Partial<OrganizationContext>>({});
  const [aiSystems, setAiSystems] = useState<AISystem[]>([]);
  const [complianceStatus, setComplianceStatus] = useState<Partial<ComplianceStatus>>({});
  const [timelineResources, setTimelineResources] = useState<Partial<TimelineResources>>({});

  // For adding new AI systems
  const [newSystemName, setNewSystemName] = useState('');
  const [newSystemDescription, setNewSystemDescription] = useState('');
  const [newSystemCategory, setNewSystemCategory] = useState<AISystemCategory>('other');
  const [editingSystemId, setEditingSystemId] = useState<string | null>(null);

  // Load saved state on mount
   
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.organization) setOrganization(parsed.organization);
        if (parsed.aiSystems) setAiSystems(parsed.aiSystems);
        if (parsed.complianceStatus) setComplianceStatus(parsed.complianceStatus);
        if (parsed.timelineResources) setTimelineResources(parsed.timelineResources);
        if (parsed.currentStep) setCurrentStep(parsed.currentStep);
      }
    } catch (e) {
      console.error('Failed to load saved assessment', e);
    }
  }, []);
   

  // Save state on changes
  useEffect(() => {
    const state: QuestionnaireState = {
      currentStep,
      organization,
      aiSystems,
      complianceStatus,
      timelineResources,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [currentStep, organization, aiSystems, complianceStatus, timelineResources]);

  const progress = (currentStep / TOTAL_STEPS) * 100;

  const nextStep = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep((currentStep + 1) as QuestionnaireStep);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as QuestionnaireStep);
    }
  };

  const addAISystem = () => {
    if (!newSystemName.trim()) return;

    const newSystem: AISystem = {
      id: `system-${Date.now()}`,
      name: newSystemName,
      description: newSystemDescription,
      category: newSystemCategory,
      purpose: '',
      affectsNaturalPersons: false,
      autonomyLevel: 'semi-automated',
      characteristics: { ...defaultCharacteristics },
    };

    setAiSystems([...aiSystems, newSystem]);
    setNewSystemName('');
    setNewSystemDescription('');
    setNewSystemCategory('other');
  };

  const removeAISystem = (id: string) => {
    setAiSystems(aiSystems.filter((s) => s.id !== id));
  };

  const updateSystemCharacteristic = (
    systemId: string,
    key: keyof AISystemCharacteristics,
    value: boolean
  ) => {
    setAiSystems(
      aiSystems.map((s) =>
        s.id === systemId
          ? { ...s, characteristics: { ...s.characteristics, [key]: value } }
          : s
      )
    );
  };

  const updateSystem = (systemId: string, updates: Partial<AISystem>) => {
    setAiSystems(
      aiSystems.map((s) => (s.id === systemId ? { ...s, ...updates } : s))
    );
  };

  const handleSubmit = () => {
    const assessment: EUAIActAssessment = {
      id: `assessment-${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      organization: organization as OrganizationContext,
      aiSystems,
      complianceStatus: complianceStatus as ComplianceStatus,
      timelineResources: timelineResources as TimelineResources,
    };

    localStorage.removeItem(STORAGE_KEY);
    onComplete(assessment);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between text-sm text-light-text-secondary dark:text-foreground-secondary mb-2">
          <span>Step {currentStep} of {TOTAL_STEPS}</span>
          <span>{Math.round(progress)}% complete</span>
        </div>
        <div className="h-2 bg-light-surface dark:bg-surface rounded-full overflow-hidden">
          <div
            className="h-full bg-accent transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Step 1: Organization Context */}
      {currentStep === 1 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-2">
              Organization Context
            </h2>
            <p className="text-light-text-secondary dark:text-foreground-secondary">
              Tell us about your organization to determine EU AI Act applicability.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-light-text dark:text-foreground mb-2">
                Where is your organization located?
              </label>
              <div className="space-y-2">
                {[
                  { value: 'eu', label: 'Within the European Union' },
                  { value: 'eu-serving', label: 'Outside EU but serves EU customers/users' },
                  { value: 'non-eu', label: 'Outside EU with no EU presence' },
                ].map((opt) => (
                  <label key={opt.value} className="flex items-center gap-3 p-3 bg-light-surface dark:bg-surface rounded-lg cursor-pointer hover:bg-light-hover dark:hover:bg-surface/70 transition-colors">
                    <input
                      type="radio"
                      name="location"
                      value={opt.value}
                      checked={organization.location === opt.value}
                      onChange={(e) => setOrganization({ ...organization, location: e.target.value as OrganizationLocation })}
                      className="text-accent focus:ring-ring"
                    />
                    <span className="text-light-text dark:text-foreground">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-light-text dark:text-foreground mb-2">
                Organization Size
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { value: 'micro', label: 'Micro/Startup', desc: '< 10 employees' },
                  { value: 'sme', label: 'SME', desc: '10-249 employees' },
                  { value: 'large', label: 'Large Enterprise', desc: '250+ employees' },
                ].map((opt) => (
                  <label key={opt.value} className={`flex flex-col p-4 rounded-lg cursor-pointer border-2 transition-colors ${organization.size === opt.value ? 'border-accent bg-accent-muted' : 'border-transparent bg-light-surface dark:bg-surface hover:border-light-border dark:hover:border-border'}`}>
                    <input
                      type="radio"
                      name="size"
                      value={opt.value}
                      checked={organization.size === opt.value}
                      onChange={(e) => setOrganization({ ...organization, size: e.target.value as OrganizationSize })}
                      className="sr-only"
                    />
                    <span className="font-semibold text-light-text dark:text-foreground">{opt.label}</span>
                    <span className="text-sm text-light-text-secondary dark:text-foreground-tertiary">{opt.desc}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-light-text dark:text-foreground mb-2">
                Primary Industry Sector
              </label>
              <select
                value={organization.sector || ''}
                onChange={(e) => setOrganization({ ...organization, sector: e.target.value as IndustrySector })}
                className="w-full p-3 bg-light-surface dark:bg-surface border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground focus:border-accent focus:ring-1 focus:ring-ring"
              >
                <option value="">Select a sector...</option>
                <option value="healthcare">Healthcare</option>
                <option value="finance">Finance & Banking</option>
                <option value="education">Education</option>
                <option value="employment">Employment & HR</option>
                <option value="law-enforcement">Law Enforcement</option>
                <option value="critical-infrastructure">Critical Infrastructure</option>
                <option value="public-services">Public Services</option>
                <option value="transport">Transport & Logistics</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-light-text dark:text-foreground mb-2">
                Your Role with AI Systems
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 bg-light-surface dark:bg-surface rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={organization.developsSystems || false}
                    onChange={(e) => setOrganization({ ...organization, developsSystems: e.target.checked })}
                    className="text-accent focus:ring-ring rounded"
                  />
                  <div>
                    <span className="text-light-text dark:text-foreground font-medium">Provider</span>
                    <span className="text-sm text-light-text-secondary dark:text-foreground-tertiary ml-2">
                      We develop or train AI systems
                    </span>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 bg-light-surface dark:bg-surface rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={organization.deploysSystems || false}
                    onChange={(e) => setOrganization({ ...organization, deploysSystems: e.target.checked })}
                    className="text-accent focus:ring-ring rounded"
                  />
                  <div>
                    <span className="text-light-text dark:text-foreground font-medium">Deployer</span>
                    <span className="text-sm text-light-text-secondary dark:text-foreground-tertiary ml-2">
                      We use or deploy AI systems in our operations
                    </span>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 bg-light-surface dark:bg-surface rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={organization.importsDistributes || false}
                    onChange={(e) => setOrganization({ ...organization, importsDistributes: e.target.checked })}
                    className="text-accent focus:ring-ring rounded"
                  />
                  <div>
                    <span className="text-light-text dark:text-foreground font-medium">Importer/Distributor</span>
                    <span className="text-sm text-light-text-secondary dark:text-foreground-tertiary ml-2">
                      We import or distribute AI systems in the EU
                    </span>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: AI System Inventory */}
      {currentStep === 2 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-2">
              AI System Inventory
            </h2>
            <p className="text-light-text-secondary dark:text-foreground-secondary">
              Add all AI systems your organization develops, deploys, or distributes.
            </p>
          </div>

          {/* Add new system form */}
          <div className="bg-light-surface dark:bg-surface rounded-lg p-4 space-y-4">
            <h3 className="font-semibold text-light-text dark:text-foreground">Add AI System</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-light-text-secondary dark:text-foreground-secondary mb-1">
                  System Name
                </label>
                <input
                  type="text"
                  value={newSystemName}
                  onChange={(e) => setNewSystemName(e.target.value)}
                  placeholder="e.g., Customer Service Chatbot"
                  className="w-full p-2 bg-white dark:bg-background border border-light-border dark:border-border rounded text-light-text dark:text-foreground"
                />
              </div>
              <div>
                <label className="block text-sm text-light-text-secondary dark:text-foreground-secondary mb-1">
                  Category
                </label>
                <select
                  value={newSystemCategory}
                  onChange={(e) => setNewSystemCategory(e.target.value as AISystemCategory)}
                  className="w-full p-2 bg-white dark:bg-background border border-light-border dark:border-border rounded text-light-text dark:text-foreground"
                >
                  <option value="chatbot">Chatbot / Conversational AI</option>
                  <option value="content-generation">Content Generation</option>
                  <option value="recommendation">Recommendation System</option>
                  <option value="analytics">Analytics / Predictions</option>
                  <option value="automation">Process Automation</option>
                  <option value="biometric-identification">Biometric Identification</option>
                  <option value="employment-workers">Employment / HR</option>
                  <option value="education-training">Education</option>
                  <option value="essential-services">Essential Services</option>
                  <option value="critical-infrastructure">Critical Infrastructure</option>
                  <option value="law-enforcement">Law Enforcement</option>
                  <option value="general-purpose">General Purpose AI</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm text-light-text-secondary dark:text-foreground-secondary mb-1">
                Description
              </label>
              <textarea
                value={newSystemDescription}
                onChange={(e) => setNewSystemDescription(e.target.value)}
                placeholder="Brief description of what this AI system does..."
                rows={2}
                className="w-full p-2 bg-white dark:bg-background border border-light-border dark:border-border rounded text-light-text dark:text-foreground"
              />
            </div>
            <button
              onClick={addAISystem}
              disabled={!newSystemName.trim()}
              className="px-4 py-2 bg-accent text-accent-foreground rounded font-semibold hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add System
            </button>
          </div>

          {/* System list */}
          {aiSystems.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-light-text dark:text-foreground">
                Your AI Systems ({aiSystems.length})
              </h3>
              {aiSystems.map((system) => (
                <div
                  key={system.id}
                  className="bg-light-surface dark:bg-surface rounded-lg p-4"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-light-text dark:text-foreground">{system.name}</h4>
                      <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary">
                        {system.category.replace(/-/g, ' ')} • {system.description || 'No description'}
                      </p>
                    </div>
                    <button
                      onClick={() => removeAISystem(system.id)}
                      className="text-red-500 hover:text-red-400 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {aiSystems.length === 0 && (
            <div className="text-center py-8 text-light-text-secondary dark:text-foreground-tertiary">
              <p>No AI systems added yet.</p>
              <p className="text-sm">Add your AI systems above to assess their risk level.</p>
            </div>
          )}

          {/* Common use cases hint */}
          {organization.sector && INDUSTRY_AI_USE_CASES[organization.sector] && (
            <div className="bg-accent-muted rounded-lg p-4">
              <h4 className="font-semibold text-light-text dark:text-foreground mb-2">
                Common AI Use Cases in {organization.sector.replace(/-/g, ' ')}
              </h4>
              <ul className="text-sm text-light-text-secondary dark:text-foreground-secondary space-y-1">
                {INDUSTRY_AI_USE_CASES[organization.sector].map((useCase, i) => (
                  <li key={i}>• {useCase}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Risk Characteristics - Per System */}
      {currentStep === 3 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-2">
              System Characteristics
            </h2>
            <p className="text-light-text-secondary dark:text-foreground-secondary">
              Answer these questions for each AI system to determine its risk category.
            </p>
          </div>

          {aiSystems.length === 0 ? (
            <div className="text-center py-8 text-light-text-secondary dark:text-foreground-tertiary">
              <p>No AI systems to assess.</p>
              <p className="text-sm">Go back and add your AI systems first.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {aiSystems.map((system) => (
                <div key={system.id} className="bg-light-surface dark:bg-surface rounded-lg p-4">
                  <h3 className="font-semibold text-light-text dark:text-foreground mb-4">
                    {system.name}
                    <span className="text-sm font-normal text-light-text-secondary dark:text-foreground-tertiary ml-2">
                      ({system.category.replace(/-/g, ' ')})
                    </span>
                  </h3>

                  <div className="space-y-4">
                    {/* Prohibited characteristics */}
                    <div>
                      <h4 className="text-sm font-medium text-red-500 mb-2">
                        Prohibited Practices (Article 5)
                      </h4>
                      <div className="space-y-2">
                        {[
                          { key: 'socialScoring', label: 'Social scoring by public authorities' },
                          { key: 'realTimeBiometricPublic', label: 'Real-time biometric identification in public spaces' },
                          { key: 'emotionRecognitionWorkplace', label: 'Emotion recognition in workplace/education' },
                          { key: 'subliminalManipulation', label: 'Subliminal manipulation techniques' },
                          { key: 'exploitsVulnerabilities', label: 'Exploits age, disability, or social vulnerabilities' },
                        ].map((item) => (
                          <label key={item.key} className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={system.characteristics[item.key as keyof AISystemCharacteristics] || false}
                              onChange={(e) => updateSystemCharacteristic(system.id, item.key as keyof AISystemCharacteristics, e.target.checked)}
                              className="text-red-500 focus:ring-red-500 rounded"
                            />
                            <span className="text-light-text dark:text-foreground">{item.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* High-risk characteristics */}
                    <div>
                      <h4 className="text-sm font-medium text-accent mb-2">
                        High-Risk Indicators (Annex III)
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {[
                          { key: 'biometricIdentification', label: 'Biometric identification' },
                          { key: 'criticalInfrastructure', label: 'Critical infrastructure safety' },
                          { key: 'educationAssessment', label: 'Education assessment/admission' },
                          { key: 'employmentDecisions', label: 'Employment/HR decisions' },
                          { key: 'essentialServicesAccess', label: 'Essential services access' },
                          { key: 'creditScoring', label: 'Credit scoring' },
                          { key: 'lawEnforcementUse', label: 'Law enforcement use' },
                          { key: 'migrationAsylum', label: 'Migration/asylum/border' },
                          { key: 'justiceAdministration', label: 'Justice administration' },
                        ].map((item) => (
                          <label key={item.key} className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={system.characteristics[item.key as keyof AISystemCharacteristics] || false}
                              onChange={(e) => updateSystemCharacteristic(system.id, item.key as keyof AISystemCharacteristics, e.target.checked)}
                              className="text-accent focus:ring-ring rounded"
                            />
                            <span className="text-light-text dark:text-foreground">{item.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Limited risk characteristics */}
                    <div>
                      <h4 className="text-sm font-medium text-yellow-500 mb-2">
                        Transparency Requirements (Article 50)
                      </h4>
                      <div className="space-y-2">
                        {[
                          { key: 'interactsWithHumans', label: 'Interacts directly with humans (chatbot, voice assistant)' },
                          { key: 'generatesContent', label: 'Generates or manipulates text, audio, images, or video' },
                          { key: 'emotionRecognition', label: 'Recognizes emotions or biometric categories' },
                          { key: 'deepfakeGeneration', label: 'Generates deep fake content' },
                        ].map((item) => (
                          <label key={item.key} className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={system.characteristics[item.key as keyof AISystemCharacteristics] || false}
                              onChange={(e) => updateSystemCharacteristic(system.id, item.key as keyof AISystemCharacteristics, e.target.checked)}
                              className="text-yellow-500 focus:ring-yellow-500 rounded"
                            />
                            <span className="text-light-text dark:text-foreground">{item.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Additional context */}
                    <div>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={system.affectsNaturalPersons}
                          onChange={(e) => updateSystem(system.id, { affectsNaturalPersons: e.target.checked })}
                          className="text-accent focus:ring-ring rounded"
                        />
                        <span className="text-light-text dark:text-foreground">
                          This system makes or influences decisions that affect individuals
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 4: Current Compliance Status */}
      {currentStep === 4 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-2">
              Current Compliance Status
            </h2>
            <p className="text-light-text-secondary dark:text-foreground-secondary">
              Tell us what compliance measures you already have in place.
            </p>
          </div>

          <div className="space-y-4">
            {[
              { key: 'hasAIGovernancePolicy', label: 'AI Governance Policy', desc: 'Documented policy for AI development and use' },
              { key: 'hasRiskManagementSystem', label: 'Risk Management System', desc: 'Systematic approach to identifying and mitigating AI risks' },
              { key: 'hasDataGovernance', label: 'Data Governance', desc: 'Policies for data quality, bias detection, and documentation' },
              { key: 'hasTechnicalDocumentation', label: 'Technical Documentation', desc: 'Comprehensive documentation of AI systems' },
              { key: 'hasHumanOversight', label: 'Human Oversight Mechanisms', desc: 'Processes for human review and intervention' },
              { key: 'hasTransparencyMeasures', label: 'Transparency Measures', desc: 'Clear disclosure to users about AI interactions' },
              { key: 'hasQualityManagement', label: 'Quality Management System', desc: 'Processes for ensuring AI system quality' },
              { key: 'hasIncidentReporting', label: 'Incident Reporting', desc: 'Procedures for reporting and handling AI incidents' },
              { key: 'hasConformityAssessment', label: 'Conformity Assessment', desc: 'Completed conformity assessments for high-risk systems' },
            ].map((item) => (
              <label
                key={item.key}
                className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer border-2 transition-colors ${
                  complianceStatus[item.key as keyof ComplianceStatus]
                    ? 'border-green-500 bg-green-500/10'
                    : 'border-transparent bg-light-surface dark:bg-surface hover:border-light-border dark:hover:border-border'
                }`}
              >
                <input
                  type="checkbox"
                  checked={complianceStatus[item.key as keyof ComplianceStatus] || false}
                  onChange={(e) => setComplianceStatus({ ...complianceStatus, [item.key]: e.target.checked })}
                  className="mt-1 text-green-500 focus:ring-green-500 rounded"
                />
                <div>
                  <span className="font-semibold text-light-text dark:text-foreground">{item.label}</span>
                  <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary">{item.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Step 5: Timeline & Resources */}
      {currentStep === 5 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-2">
              Timeline & Resources
            </h2>
            <p className="text-light-text-secondary dark:text-foreground-secondary">
              Help us understand your implementation capacity.
            </p>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-light-text dark:text-foreground mb-2">
                Implementation Urgency
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { value: 'immediate', label: 'Immediate', desc: 'Need to comply ASAP' },
                  { value: 'within-6-months', label: 'Within 6 months', desc: 'Planning for near-term' },
                  { value: 'within-1-year', label: 'Within 1 year', desc: 'Strategic planning' },
                  { value: 'planning', label: 'Just planning', desc: 'Future consideration' },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex flex-col p-4 rounded-lg cursor-pointer border-2 transition-colors ${
                      timelineResources.implementationUrgency === opt.value
                        ? 'border-accent bg-accent-muted'
                        : 'border-transparent bg-light-surface dark:bg-surface hover:border-light-border dark:hover:border-border'
                    }`}
                  >
                    <input
                      type="radio"
                      name="urgency"
                      value={opt.value}
                      checked={timelineResources.implementationUrgency === opt.value}
                      onChange={(e) => setTimelineResources({ ...timelineResources, implementationUrgency: e.target.value as any })}
                      className="sr-only"
                    />
                    <span className="font-semibold text-light-text dark:text-foreground">{opt.label}</span>
                    <span className="text-sm text-light-text-secondary dark:text-foreground-tertiary">{opt.desc}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-light-text dark:text-foreground mb-2">
                Available Budget for Compliance
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { value: 'limited', label: 'Limited', desc: 'Minimal additional investment' },
                  { value: 'moderate', label: 'Moderate', desc: 'Some budget allocated' },
                  { value: 'substantial', label: 'Substantial', desc: 'Significant investment possible' },
                  { value: 'undefined', label: 'Not defined', desc: 'Budget not yet determined' },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex flex-col p-4 rounded-lg cursor-pointer border-2 transition-colors ${
                      timelineResources.budgetAvailable === opt.value
                        ? 'border-accent bg-accent-muted'
                        : 'border-transparent bg-light-surface dark:bg-surface hover:border-light-border dark:hover:border-border'
                    }`}
                  >
                    <input
                      type="radio"
                      name="budget"
                      value={opt.value}
                      checked={timelineResources.budgetAvailable === opt.value}
                      onChange={(e) => setTimelineResources({ ...timelineResources, budgetAvailable: e.target.value as any })}
                      className="sr-only"
                    />
                    <span className="font-semibold text-light-text dark:text-foreground">{opt.label}</span>
                    <span className="text-sm text-light-text-secondary dark:text-foreground-tertiary">{opt.desc}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-light-text dark:text-foreground mb-2">
                Internal AI/Compliance Expertise
              </label>
              <div className="space-y-2">
                {[
                  { value: 'none', label: 'None', desc: 'No dedicated expertise' },
                  { value: 'some', label: 'Some', desc: 'Part-time or shared resources' },
                  { value: 'dedicated-team', label: 'Dedicated Team', desc: 'Full-time AI governance team' },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border-2 transition-colors ${
                      timelineResources.internalExpertise === opt.value
                        ? 'border-accent bg-accent-muted'
                        : 'border-transparent bg-light-surface dark:bg-surface hover:border-light-border dark:hover:border-border'
                    }`}
                  >
                    <input
                      type="radio"
                      name="expertise"
                      value={opt.value}
                      checked={timelineResources.internalExpertise === opt.value}
                      onChange={(e) => setTimelineResources({ ...timelineResources, internalExpertise: e.target.value as any })}
                      className="sr-only"
                    />
                    <div>
                      <span className="font-semibold text-light-text dark:text-foreground">{opt.label}</span>
                      <span className="text-sm text-light-text-secondary dark:text-foreground-tertiary ml-2">{opt.desc}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="flex items-center gap-3 p-4 bg-light-surface dark:bg-surface rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={timelineResources.externalSupport || false}
                  onChange={(e) => setTimelineResources({ ...timelineResources, externalSupport: e.target.checked })}
                  className="text-accent focus:ring-ring rounded"
                />
                <div>
                  <span className="font-semibold text-light-text dark:text-foreground">Open to External Support</span>
                  <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary">
                    Would consider consultants or third-party services for compliance
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Step 6: Review */}
      {currentStep === 6 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-2">
              Review Your Assessment
            </h2>
            <p className="text-light-text-secondary dark:text-foreground-secondary">
              Review the information before generating your compliance assessment.
            </p>
          </div>

          {/* Organization Summary */}
          <div className="bg-light-surface dark:bg-surface rounded-lg p-4">
            <h3 className="font-semibold text-light-text dark:text-foreground mb-3">Organization</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-light-text-secondary dark:text-foreground-tertiary">Location:</div>
              <div className="text-light-text dark:text-foreground capitalize">{organization.location?.replace(/-/g, ' ') || 'Not specified'}</div>
              <div className="text-light-text-secondary dark:text-foreground-tertiary">Size:</div>
              <div className="text-light-text dark:text-foreground capitalize">{organization.size || 'Not specified'}</div>
              <div className="text-light-text-secondary dark:text-foreground-tertiary">Sector:</div>
              <div className="text-light-text dark:text-foreground capitalize">{organization.sector?.replace(/-/g, ' ') || 'Not specified'}</div>
            </div>
          </div>

          {/* AI Systems Summary */}
          <div className="bg-light-surface dark:bg-surface rounded-lg p-4">
            <h3 className="font-semibold text-light-text dark:text-foreground mb-3">
              AI Systems ({aiSystems.length})
            </h3>
            {aiSystems.length > 0 ? (
              <ul className="space-y-2 text-sm">
                {aiSystems.map((system) => (
                  <li key={system.id} className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-accent rounded-full" />
                    <span className="text-light-text dark:text-foreground">{system.name}</span>
                    <span className="text-light-text-secondary dark:text-foreground-tertiary">
                      ({system.category.replace(/-/g, ' ')})
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-light-text-secondary dark:text-foreground-tertiary text-sm">No AI systems added</p>
            )}
          </div>

          {/* Compliance Status Summary */}
          <div className="bg-light-surface dark:bg-surface rounded-lg p-4">
            <h3 className="font-semibold text-light-text dark:text-foreground mb-3">Compliance Measures</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(complianceStatus).filter(([_, v]) => v).length > 0 ? (
                Object.entries(complianceStatus)
                  .filter(([_, v]) => v)
                  .map(([key]) => (
                    <span key={key} className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
                      {key.replace(/has/g, '').replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  ))
              ) : (
                <p className="text-light-text-secondary dark:text-foreground-tertiary text-sm">No measures in place</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between mt-8 pt-6 border-t border-light-border dark:border-border">
        <button
          onClick={prevStep}
          disabled={currentStep === 1}
          className="px-6 py-3 border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground hover:bg-light-hover dark:hover:bg-cream/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back
        </button>

        {currentStep < TOTAL_STEPS ? (
          <button
            onClick={nextStep}
            className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent-hover transition-colors"
          >
            Continue
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent-hover transition-colors"
          >
            Generate Assessment
          </button>
        )}
      </div>
    </div>
  );
}
